import { api } from "../api";
import { useApiData } from "../useApi";
import { PageState, Stale, formatTime } from "../components";

export function Overview({ onSignedOut }: { onSignedOut: () => void }) {
  const { data, error, loading, reload } = useApiData(() => api.overview(), onSignedOut);

  return (
    <>
      <h1>Overview</h1>
      <p className="subtitle">
        Account state as the EA last reported it.{" "}
        <button className="link" onClick={reload}>
          Refresh
        </button>
      </p>

      <PageState loading={loading} error={error} empty={data?.accounts.length === 0}>
        {data && (
          <>
            {data.live_trading_hard_disabled && (
              <div className="notice">
                Live trading is hard-disabled in this build. Commands can only reach
                paper/demo accounts.
              </div>
            )}

            {data.accounts.map((account) => {
              const a = account.account;
              return (
                <div className="card" key={account.account_ref}>
                  <h2>
                    {account.account_ref} · {account.environment}
                    {account.ea_mode && <> · {account.ea_mode}</>}
                  </h2>

                  {a ? (
                    <div className="metrics">
                      <Metric label="Equity" value={a.equity} />
                      <Metric label="Balance" value={a.balance} />
                      <Metric label="Free margin" value={a.margin_free} />
                      <Metric label="Margin" value={a.margin} />
                      <Metric label="Currency" value={a.currency} />
                      <Metric label="Leverage" value={a.leverage} />
                    </div>
                  ) : (
                    <p className="dim">
                      No report received yet — the EA has not sent an account snapshot.
                    </p>
                  )}

                  <p className="dim" style={{ marginTop: 14, marginBottom: 0 }}>
                    {a?.broker_name && <>Broker: {a.broker_name} · </>}
                    Last report:{" "}
                    <Stale at={account.last_report_at} serverTime={data.server_time} /> ·
                    Last heartbeat:{" "}
                    <Stale at={account.last_heartbeat_at} serverTime={data.server_time} />
                  </p>

                  <h2 style={{ marginTop: 20 }}>
                    Open positions ({account.positions.length})
                  </h2>
                  {account.positions.length === 0 ? (
                    <p className="dim" style={{ margin: 0 }}>
                      None.
                    </p>
                  ) : (
                    <table>
                      <thead>
                        <tr>
                          <th>Ticket</th>
                          <th>Symbol</th>
                          <th>Side</th>
                          <th>Volume</th>
                          <th>Entry</th>
                          <th>SL</th>
                          <th>TP</th>
                          <th>Profit</th>
                          <th>Opened</th>
                        </tr>
                      </thead>
                      <tbody>
                        {account.positions.map((p) => {
                          const row = p as Record<string, string | null>;
                          return (
                            <tr key={String(row.ticket)}>
                              <td className="mono">{row.ticket}</td>
                              <td>{row.provider_symbol}</td>
                              <td>{row.side}</td>
                              <td className="mono">{row.volume}</td>
                              <td className="mono">{row.entry_price}</td>
                              <td className="mono">{row.stop_loss ?? "—"}</td>
                              <td className="mono">{row.take_profit ?? "—"}</td>
                              <td className="mono">{row.profit}</td>
                              <td className="dim">{formatTime(row.opened_at)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </>
        )}
      </PageState>
    </>
  );
}

function Metric({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div className="metric">
      <div className="label">{label}</div>
      <div className="value">{value ?? "—"}</div>
    </div>
  );
}
